import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import {
  cancelErasure,
  createGdprExport,
  downloadGdprExport,
  fetchBusinesses,
  fetchErasureQueue,
  fetchGdprAccessLog,
  fetchGdprExports,
  fetchGdprOverview,
  fetchGdprPolicy,
  rescheduleErasure,
  scheduleErasure,
} from './api';
import type { AccessLogEntry, ErasureQueueItem, GdprExportRequest, GdprOverview, PlatformBusiness } from './types';
import { daysUntil, fmtDate, statusStyle } from './utils';

type Tab = 'erasure' | 'exports' | 'access' | 'policy';

const GdprPage: React.FC = () => {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as Tab) || 'erasure';

  const [overview, setOverview] = useState<GdprOverview | null>(null);
  const [queue, setQueue] = useState<ErasureQueueItem[]>([]);
  const [exports, setExports] = useState<GdprExportRequest[]>([]);
  const [accessLog, setAccessLog] = useState<AccessLogEntry[]>([]);
  const [businesses, setBusinesses] = useState<PlatformBusiness[]>([]);
  const [policy, setPolicy] = useState<{ policy: GdprOverview['policy']; summary: string[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [accessSource, setAccessSource] = useState('all');
  const [accessBizFilter, setAccessBizFilter] = useState('');

  const [scheduleForm, setScheduleForm] = useState({ business_id: '', reason: '', days: '30' });
  const [exportForm, setExportForm] = useState({
    business_id: '',
    scope: 'business' as 'business' | 'tenant',
    tenant_id: '',
    date_from: '',
    date_to: '',
    purpose: '',
  });
  const [rescheduleDates, setRescheduleDates] = useState<Record<number, string>>({});

  const setTab = (t: Tab) => {
    setSearchParams({ tab: t }, { replace: true });
  };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [ov, q, ex, biz] = await Promise.all([
        fetchGdprOverview(token),
        fetchErasureQueue(token),
        fetchGdprExports(token),
        fetchBusinesses(token),
      ]);
      setOverview(ov);
      setQueue(q);
      setExports(ex);
      setBusinesses(biz);
    } catch {
      toast.error('Failed to load GDPR data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadAccess = useCallback(async () => {
    if (!token) return;
    try {
      setAccessLog(await fetchGdprAccessLog(token, {
        source: accessSource,
        business_id: accessBizFilter ? parseInt(accessBizFilter, 10) : undefined,
        limit: 150,
      }));
    } catch {
      toast.error('Failed to load access log');
    }
  }, [token, accessSource, accessBizFilter]);

  const loadPolicy = useCallback(async () => {
    if (!token) return;
    try {
      setPolicy(await fetchGdprPolicy(token));
    } catch {
      toast.error('Failed to load policy');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 'access') loadAccess(); }, [tab, loadAccess]);
  useEffect(() => { if (tab === 'policy') loadPolicy(); }, [tab, loadPolicy]);

  const onCancelErasure = async (id: number, name: string) => {
    if (!token || !window.confirm(`Cancel scheduled erasure for ${name}?`)) return;
    setBusy(true);
    try {
      await cancelErasure(token, id);
      toast.success('Erasure cancelled');
      await load();
    } catch {
      toast.error('Cancel failed');
    } finally {
      setBusy(false);
    }
  };

  const onReschedule = async (id: number) => {
    const date = rescheduleDates[id];
    if (!token || !date) return toast.error('Pick a new deletion date');
    setBusy(true);
    try {
      await rescheduleErasure(token, id, new Date(date).toISOString());
      toast.success('Erasure rescheduled');
      await load();
    } catch {
      toast.error('Reschedule failed');
    } finally {
      setBusy(false);
    }
  };

  const onSchedule = async () => {
    if (!token) return;
    const businessId = parseInt(scheduleForm.business_id, 10);
    if (Number.isNaN(businessId) || !scheduleForm.reason.trim()) {
      return toast.error('Business ID and reason are required');
    }
    if (!window.confirm('Schedule account erasure? This will cancel subscription access.')) return;
    setBusy(true);
    try {
      await scheduleErasure(token, businessId, scheduleForm.reason.trim(), parseInt(scheduleForm.days, 10) || 30);
      toast.success('Erasure scheduled');
      setScheduleForm({ business_id: '', reason: '', days: '30' });
      await load();
      setTab('erasure');
    } catch {
      toast.error('Schedule failed');
    } finally {
      setBusy(false);
    }
  };

  const onCreateExport = async () => {
    if (!token) return;
    const businessId = parseInt(exportForm.business_id, 10);
    if (Number.isNaN(businessId) || !exportForm.purpose.trim()) {
      return toast.error('Business ID and purpose (legal basis) are required');
    }
    setBusy(true);
    try {
      await createGdprExport(token, {
        business_id: businessId,
        scope: exportForm.scope,
        tenant_id: exportForm.tenant_id ? parseInt(exportForm.tenant_id, 10) : undefined,
        date_from: exportForm.date_from || undefined,
        date_to: exportForm.date_to || undefined,
        purpose: exportForm.purpose.trim(),
      });
      toast.success('Export completed');
      setExportForm({ business_id: '', scope: 'business', tenant_id: '', date_from: '', date_to: '', purpose: '' });
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setBusy(false);
    }
  };

  const onDownload = async (row: GdprExportRequest) => {
    if (!token || row.status !== 'completed') return;
    try {
      await downloadGdprExport(token, row.id, `export-b${row.business_id}-${row.id.slice(0, 8)}.json`);
    } catch {
      toast.error('Download failed');
    }
  };

  if (loading && !overview) return <div className="plat-empty">Loading GDPR tools…</div>;

  return (
    <>
      <div className="plat-header">
        <div>
          <h2>GDPR &amp; data protection</h2>
          <p>Erasure queue, DSAR exports, access log — wired to existing deletion automation.</p>
        </div>
        <button type="button" className="plat-btn plat-btn-ghost" onClick={load}>Refresh</button>
      </div>

      {overview && (
        <div className="plat-kpi-row">
          <div className="plat-kpi warn"><div className="kl">Scheduled erasure</div><div className="kv">{overview.scheduledErasure}</div></div>
          <div className="plat-kpi"><div className="kl">Completed exports</div><div className="kv">{overview.completedExports}</div></div>
          <div className="plat-kpi"><div className="kl">DSAR SLA</div><div className="kv" style={{ fontSize: 22 }}>{overview.policy.dsarSlaDays}d</div></div>
          <div className="plat-kpi"><div className="kl">Deletion grace</div><div className="kv" style={{ fontSize: 22 }}>{overview.policy.gdprDeletionGraceDays}d</div></div>
        </div>
      )}

      <div className="plat-pills" style={{ marginBottom: 18 }}>
        {(['erasure', 'exports', 'access', 'policy'] as Tab[]).map((t) => (
          <button key={t} type="button" className={`plat-pill${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {tab === 'erasure' && (
        <>
          <div className="plat-panel">
            <h3>Erasure queue</h3>
            <p className="plat-muted" style={{ marginTop: 0, marginBottom: 14 }}>
              Azure timer runs daily — deletes when <code>deletion_scheduled_at</code> is reached. Cancel or reschedule here.
            </p>
            {queue.length === 0 ? (
              <p className="plat-muted">No pending erasures.</p>
            ) : (
              <div className="plat-table-wrap">
                <table className="plat-table">
                  <thead>
                    <tr>
                      <th>Business</th>
                      <th>Owner</th>
                      <th>Deletes on</th>
                      <th>Days left</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {queue.map((row) => {
                      const left = daysUntil(row.deletion_scheduled_at);
                      return (
                        <tr key={row.id}>
                          <td><strong>{row.name}</strong></td>
                          <td className="plat-muted">{row.owner_email || '—'}</td>
                          <td>{fmtDate(row.deletion_scheduled_at)}</td>
                          <td>{left !== null ? (left <= 0 ? 'Due now' : `${left}d`) : '—'}</td>
                          <td>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                              <input
                                type="date"
                                className="plat-search"
                                style={{ maxWidth: 140, padding: '6px 8px', margin: 0 }}
                                value={rescheduleDates[row.id]?.slice(0, 10) || ''}
                                onChange={(e) => setRescheduleDates((s) => ({ ...s, [row.id]: e.target.value }))}
                              />
                              <button type="button" className="plat-btn plat-btn-ghost plat-btn-sm" disabled={busy} onClick={() => onReschedule(row.id)}>Reschedule</button>
                              <button type="button" className="plat-btn plat-btn-danger plat-btn-sm" disabled={busy} onClick={() => onCancelErasure(row.id, row.name)}>Cancel</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="plat-panel">
            <h3>Schedule erasure</h3>
            <p className="plat-muted" style={{ marginTop: 0 }}>Requires reason for audit. Suspends subscription access.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 10 }}>
              <select className="plat-select" value={scheduleForm.business_id} onChange={(e) => setScheduleForm((s) => ({ ...s, business_id: e.target.value }))}>
                <option value="">Select business…</option>
                {businesses.map((b) => <option key={b.id} value={b.id}>{b.name} (#{b.id})</option>)}
              </select>
              <input className="plat-search" placeholder="Reason (required)" value={scheduleForm.reason} onChange={(e) => setScheduleForm((s) => ({ ...s, reason: e.target.value }))} />
              <input className="plat-search" type="number" min={1} max={90} placeholder="Days until delete" value={scheduleForm.days} onChange={(e) => setScheduleForm((s) => ({ ...s, days: e.target.value }))} />
            </div>
            <button type="button" className="plat-btn plat-btn-danger" disabled={busy} onClick={onSchedule}>Schedule erasure</button>
          </div>
        </>
      )}

      {tab === 'exports' && (
        <>
          <div className="plat-panel">
            <h3>Request data export (DSAR)</h3>
            <p className="plat-muted" style={{ marginTop: 0 }}>Business-wide or single tenant/customer. Passwords never included.</p>
            <p className="plat-muted" style={{ margin: '0 0 12px', fontSize: 13, color: '#94a3b8' }}>
              Tenant-scoped exports require an active access case — open one from <strong>Businesses</strong> or <strong>System → Access cases</strong> (4h TTL).
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 10 }}>
              <select className="plat-select" value={exportForm.business_id} onChange={(e) => setExportForm((s) => ({ ...s, business_id: e.target.value }))}>
                <option value="">Business…</option>
                {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <select className="plat-select" value={exportForm.scope} onChange={(e) => setExportForm((s) => ({ ...s, scope: e.target.value as 'business' | 'tenant' }))}>
                <option value="business">Whole business</option>
                <option value="tenant">Single tenant ID</option>
              </select>
              {exportForm.scope === 'tenant' && (
                <input className="plat-search" placeholder="Tenant/customer ID" value={exportForm.tenant_id} onChange={(e) => setExportForm((s) => ({ ...s, tenant_id: e.target.value }))} />
              )}
              <input className="plat-search" type="date" value={exportForm.date_from} onChange={(e) => setExportForm((s) => ({ ...s, date_from: e.target.value }))} />
              <input className="plat-search" type="date" value={exportForm.date_to} onChange={(e) => setExportForm((s) => ({ ...s, date_to: e.target.value }))} />
              <input className="plat-search" placeholder="Purpose / legal basis *" value={exportForm.purpose} onChange={(e) => setExportForm((s) => ({ ...s, purpose: e.target.value }))} />
            </div>
            <button type="button" className="plat-btn plat-btn-primary" disabled={busy} onClick={onCreateExport}>Generate export</button>
          </div>

          <div className="plat-panel" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px' }}><h3 style={{ margin: 0 }}>Export history</h3></div>
            {exports.length === 0 ? (
              <div className="plat-empty">No exports yet.</div>
            ) : (
              <div className="plat-table-wrap">
                <table className="plat-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Business</th>
                      <th>Scope</th>
                      <th>Status</th>
                      <th>Rows</th>
                      <th>By</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {exports.map((row) => (
                      <tr key={row.id}>
                        <td className="plat-muted">{fmtDate(row.created_at)}</td>
                        <td>{row.business_name || `#${row.business_id}`}</td>
                        <td>{row.scope}{row.tenant_id ? ` #${row.tenant_id}` : ''}</td>
                        <td><span style={statusStyle(row.status === 'completed' ? 'active' : row.status === 'failed' ? 'expired' : 'trial')}>{row.status}</span></td>
                        <td>{row.row_count ?? '—'}</td>
                        <td className="plat-muted">{row.requested_by}</td>
                        <td>
                          {row.status === 'completed' && (
                            <button type="button" className="plat-btn plat-btn-ghost plat-btn-sm" onClick={() => onDownload(row)}>Download</button>
                          )}
                          {row.status === 'failed' && <span className="plat-muted" title={row.error_message}>Failed</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {tab === 'access' && (
        <div className="plat-panel">
          <h3>Access log</h3>
          <p className="plat-muted" style={{ marginTop: 0 }}>Platform admin actions + tenant audit events.</p>
          <div className="plat-toolbar">
            <div className="plat-pills">
              {['all', 'admin', 'audit'].map((s) => (
                <button key={s} type="button" className={`plat-pill${accessSource === s ? ' active' : ''}`} onClick={() => setAccessSource(s)}>{s}</button>
              ))}
            </div>
            <input className="plat-search" style={{ maxWidth: 120 }} placeholder="Business ID" value={accessBizFilter} onChange={(e) => setAccessBizFilter(e.target.value)} />
            <button type="button" className="plat-btn plat-btn-ghost plat-btn-sm" onClick={loadAccess}>Apply</button>
          </div>
          {accessLog.length === 0 ? (
            <p className="plat-muted">No entries.</p>
          ) : (
            <div className="plat-table-wrap">
              <table className="plat-table">
                <thead>
                  <tr><th>When</th><th>Source</th><th>Action</th><th>Actor</th><th>Business</th><th>Detail</th></tr>
                </thead>
                <tbody>
                  {accessLog.map((e, i) => (
                    <tr key={`${e.source}-${e.id}-${i}`}>
                      <td className="plat-muted">{fmtDate(e.created_at)}</td>
                      <td>{e.source}</td>
                      <td>{e.action}</td>
                      <td>{e.actor || '—'}</td>
                      <td>{e.business_id || '—'}</td>
                      <td className="plat-muted" style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.detail}>{e.detail || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'policy' && policy && (
        <div className="plat-panel">
          <h3>Retention &amp; privacy policy</h3>
          <ul style={{ lineHeight: 1.7, color: '#cbd5e1', paddingLeft: 20 }}>
            {policy.summary.map((line) => <li key={line}>{line}</li>)}
          </ul>
          <div className="plat-detail-row"><span className="label">Admin log retention</span><span>Last {policy.policy.adminLogRetentionRows} entries</span></div>
          <div className="plat-detail-row"><span className="label">Post-cancel deletion grace</span><span>{policy.policy.gdprDeletionGraceDays} days</span></div>
          <div className="plat-detail-row"><span className="label">Invoice retention</span><span>{policy.policy.invoiceRetentionYears} years (anonymised)</span></div>
          <div className="plat-detail-row"><span className="label">DSAR SLA</span><span>{policy.policy.dsarSlaDays} days</span></div>
          <div className="plat-detail-row"><span className="label">Privacy contact</span><span>{policy.policy.privacyEmail}</span></div>
          <div className="plat-detail-row"><span className="label">Privacy policy</span><span><a href={policy.policy.privacyUrl} target="_blank" rel="noreferrer" style={{ color: '#a5b4fc' }}>{policy.policy.privacyUrl}</a></span></div>
        </div>
      )}
    </>
  );
};

export default GdprPage;
